import { useEffect } from "react";

function ModalResultadoCargaMasiva({
  isOpen,
  view,
  result,
  errorMessage,
  onClose,
}) {
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const closeIfBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const isErrorView = view === "error";
  const isResultView = view === "result" && result;
  const hasImportErrors =
    Array.isArray(result?.failed) && result.failed.length > 0;

  const title = isErrorView
    ? "No se pudo procesar el archivo"
    : result?.ok === false
    ? "No se importó nada"
    : "Resultado de la carga";

  const subtitle = isErrorView
    ? "Revisa el archivo e inténtalo nuevamente."
    : result?.ok === false
    ? "El archivo fue procesado, pero no se pudo importar ningún registro."
    : "La importación finalizó correctamente.";

  const createdCount = result?.created_count ?? 0;
  const failedCount = result?.failed_count ?? 0;

  return (
    <>
      <div className="crm-modal-backdrop" onMouseDown={closeIfBackdrop}>
        <div
          className="crm-modal"
          style={{ maxWidth: view === "result" ? 640 : 500 }}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="crm-modal-title"
        >
          <button
            type="button"
            className="crm-modal-close"
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            ×
          </button>

          <div className="crm-modal-header">
            <div
              className={`crm-modal-icon ${
                isErrorView || result?.ok === false
                  ? "crm-modal-icon-error"
                  : "crm-modal-icon-success"
              }`}
            >
              {isErrorView || result?.ok === false ? "!" : "✓"}
            </div>

            <div className="crm-modal-header-text">
              <h3 id="crm-modal-title" className="crm-modal-title">
                {title}
              </h3>
              <p className="crm-modal-subtitle">{subtitle}</p>
            </div>
          </div>

          {isErrorView && (
            <>
              <div className="crm-alert crm-alert-error">
                {errorMessage || "Error desconocido."}
              </div>

              <div className="crm-modal-actions">
                <button type="button" className="btn-primary" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </>
          )}

          {isResultView && (
            <>
              {result.ok === false && result.message && (
                <div className="crm-alert crm-alert-error">{result.message}</div>
              )}

              <div className="crm-summary-grid">
                <div className="crm-summary-card">
                  <span className="crm-summary-label">Registros creados</span>
                  <strong className="crm-summary-value crm-summary-success">
                    {createdCount}
                  </strong>
                </div>

                <div className="crm-summary-card">
                  <span className="crm-summary-label">Registros con error</span>
                  <strong className="crm-summary-value crm-summary-error">
                    {failedCount}
                  </strong>
                </div>
              </div>

              {hasImportErrors && (
                <div className="crm-error-box">
                  <div className="crm-error-box-header">
                    <span>Detalle de errores</span>
                    <span className="crm-error-badge">
                      {result.failed.length} fila(s)
                    </span>
                  </div>

                  <div className="crm-error-list">
                    {result.failed.map((f) => (
                      <div key={f.line} className="crm-error-item">
                        <div className="crm-error-item-title">
                          Fila {f.line}
                        </div>

                        <ul className="crm-error-item-list">
                          {(f.errors || []).map((err, i) => (
                            <li key={i}>
                              {err.field ? (
                                <span className="crm-error-field">
                                  [{err.field}]
                                </span>
                              ) : null}{" "}
                              {err.msg || err.message || "Error"}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="crm-modal-actions">
                <button type="button" className="btn-primary" onClick={onClose}>
                  Aceptar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .crm-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(6px);
          animation: crmFadeIn 0.18s ease-out;
        }

        .crm-modal {
          position: relative;
          width: 100%;
          background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(148, 163, 184, 0.22);
          border-radius: 24px;
          padding: 24px;
          box-shadow:
            0 24px 80px rgba(15, 23, 42, 0.22),
            0 8px 24px rgba(15, 23, 42, 0.08);
          animation: crmScaleIn 0.2s ease-out;
        }

        .crm-modal-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 999px;
          background: #eef2f7;
          color: #475569;
          font-size: 20px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .crm-modal-close:hover {
          background: #e2e8f0;
          color: #0f172a;
          transform: scale(1.04);
        }

        .crm-modal-header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 20px;
          padding-right: 40px;
        }

        .crm-modal-icon {
          flex: 0 0 auto;
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 800;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }

        .crm-modal-icon-success {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #15803d;
        }

        .crm-modal-icon-error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #b91c1c;
        }

        .crm-modal-header-text {
          flex: 1;
          min-width: 0;
        }

        .crm-modal-title {
          margin: 0 0 6px 0;
          font-size: 22px;
          line-height: 1.15;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .crm-modal-subtitle {
          margin: 0;
          font-size: 14px;
          line-height: 1.55;
          color: #64748b;
        }

        .crm-alert {
          border-radius: 14px;
          padding: 14px 16px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .crm-alert-error {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }

        .crm-summary-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 18px;
        }

        .crm-summary-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 16px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
        }

        .crm-summary-label {
          display: block;
          font-size: 13px;
          color: #64748b;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .crm-summary-value {
          display: block;
          font-size: 28px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .crm-summary-success {
          color: #15803d;
        }

        .crm-summary-error {
          color: #b91c1c;
        }

        .crm-error-box {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          margin-bottom: 18px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
        }

        .crm-error-box-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }

        .crm-error-badge {
          background: #e2e8f0;
          color: #334155;
          border-radius: 999px;
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .crm-error-list {
          max-height: 260px;
          overflow-y: auto;
          padding: 14px 16px;
          background: #ffffff;
        }

        .crm-error-item {
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .crm-error-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .crm-error-item:first-child {
          padding-top: 0;
        }

        .crm-error-item-title {
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 8px;
        }

        .crm-error-item-list {
          margin: 0;
          padding-left: 18px;
          color: #475569;
          font-size: 13px;
          line-height: 1.6;
        }

        .crm-error-item-list li + li {
          margin-top: 4px;
        }

        .crm-error-field {
          color: #7c3aed;
          font-weight: 700;
        }

        .crm-modal-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
        }

        @keyframes crmFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes crmScaleIn {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 640px) {
          .crm-modal {
            padding: 18px;
            border-radius: 20px;
          }

          .crm-modal-header {
            gap: 12px;
            margin-bottom: 18px;
          }

          .crm-modal-icon {
            width: 46px;
            height: 46px;
            border-radius: 14px;
            font-size: 20px;
          }

          .crm-modal-title {
            font-size: 19px;
          }

          .crm-summary-grid {
            grid-template-columns: 1fr;
          }

          .crm-modal-actions {
            justify-content: stretch;
          }

          .crm-modal-actions .btn-primary {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
}

export default ModalResultadoCargaMasiva;